<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useDevLogStore } from '../stores/devLog.js'
import DevJsonTree from './DevJsonTree.vue'
import { formatTime } from '../utils/formatters.js'

const store      = useDevLogStore()
const expand     = ref(null)
const copied     = ref(null)
const copiedClaude = ref(false)
const snapping   = ref(false)
const search     = ref('')
const qOpen      = ref({})   // per-entry query list open state (default open)
const qSearch    = ref({})   // per-entry SQL filter text

function qIsOpen(id)    { return qOpen.value[id] !== false }   // default open
function qToggle(id, e) { e.stopPropagation(); qOpen.value[id] = !qIsOpen(id) }
function qFilter(e, queries) {
    const q = (qSearch.value[e.id] ?? '').trim().toLowerCase()
    return q ? queries.filter(r => r.sql?.toLowerCase().includes(q)) : queries
}

// ── Draggable button — default bottom-left ────────────────────────────────────
const pos      = ref({ x: 12, y: window.innerHeight - 52 })
const dragging = ref(false)
let dragOffset = { x: 0, y: 0 }

function onDragStart(e) {
    dragging.value = true
    const t = e.touches?.[0] ?? e
    dragOffset = { x: t.clientX - pos.value.x, y: t.clientY - pos.value.y }
    e.preventDefault()
}
function onDragMove(e) {
    if (!dragging.value) return
    const t = e.touches?.[0] ?? e
    pos.value = {
        x: Math.max(0, Math.min(window.innerWidth  - 72, t.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - 40, t.clientY - dragOffset.y)),
    }
}
function onDragEnd() { dragging.value = false }

onMounted(() => {
    window.addEventListener('mousemove', onDragMove)
    window.addEventListener('mouseup',   onDragEnd)
    window.addEventListener('touchmove', onDragMove, { passive: false })
    window.addEventListener('touchend',  onDragEnd)
})
onUnmounted(() => {
    window.removeEventListener('mousemove', onDragMove)
    window.removeEventListener('mouseup',   onDragEnd)
    window.removeEventListener('touchmove', onDragMove)
    window.removeEventListener('touchend',  onDragEnd)
})

// ── Snapshot ──────────────────────────────────────────────────────────────────
async function takeSnapshot() {
    snapping.value = true
    try {
        const res  = await fetch('/api/v1/__dev/snapshot', {
            credentials: 'include',
            headers: { 'Accept': 'application/json', 'X-Dev-Debug': '1', 'X-XSRF-TOKEN': getCsrf() },
        })
        const json = await res.json()
        store.entries.unshift({
            id:     crypto.randomUUID(),
            time:   formatTime(),
            type:   'snap',
            label:  `SNAPSHOT — session, auth, queries (${json.data?.queries?.length ?? 0} queries, ${json.data?.memory?.current_human ?? '?'})`,
            detail: json.data,
            status: null,
        })
        store.open = true
        expand.value = store.entries[0].id
    } catch (e) {
        alert('Snapshot failed: ' + e.message)
    } finally {
        snapping.value = false
    }
}

function getCsrf() {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/)
    return match ? decodeURIComponent(match[1]) : ''
}

// ── Log display ───────────────────────────────────────────────────────────────
const filters = ['all','api','php','snap','lumi','fn','reverb','error','evt']

const filtered = computed(() => {
    const f = store.filter
    let list = store.entries
    if (f === 'error') list = list.filter(e => e.type === 'err')
    else if (f === 'evt')   list = list.filter(e => e.type === 'evt')
    else if (f === 'lumi')  list = list.filter(e => e.type === 'lumi')
    else if (f === 'fn')    list = list.filter(e => e.type === 'fn')
    else if (f === 'reverb') list = list.filter(e => e.type === 'reverb')
    else if (f === 'php')   list = list.filter(e => e.type === 'php')
    else if (f === 'snap')  list = list.filter(e => e.type === 'snap')
    else if (f === 'api')   list = list.filter(e => e.type === 'req' || e.type === 'res')

    const q = search.value.trim().toLowerCase()
    if (!q) return list
    return list.filter(e => {
        if (e.label?.toLowerCase().includes(q)) return true
        try { return JSON.stringify(e.detail).toLowerCase().includes(q) } catch { return false }
    })
})

// Source chip — what system made/handled this call
function sourceChip(e) {
    if (e.type === 'snap') return { text: 'SNAP',   cls: 'bg-indigo-700 text-indigo-200' }
    if (e.type === 'evt')  return { text: 'EVT',    cls: 'bg-purple-800 text-purple-200' }
    if (e.type === 'lumi') return { text: 'LUMI',   cls: 'bg-violet-700 text-violet-200' }
    if (e.type === 'fn')   return { text: 'FN',    cls: 'bg-emerald-800 text-emerald-200' }
    if (e.type === 'reverb') return { text: 'WS',  cls: 'bg-cyan-700 text-cyan-200' }
    if (e.type === 'php')  return { text: 'PHP',    cls: 'bg-amber-700 text-amber-200' }
    const src = e.source ?? (e.type === 'req' ? 'REQ' : 'API')
    const MAP = {
        REVERB: 'bg-violet-800 text-violet-200',
        AUTH:   'bg-rose-800 text-rose-200',
        API:    'bg-cyan-900 text-cyan-300',
        REQ:    'bg-cyan-800 text-cyan-200',
        HTTP:   'bg-slate-700 text-slate-300',
    }
    return { text: src, cls: MAP[src] ?? 'bg-slate-700 text-slate-300' }
}

// Status chip — HTTP response code (null = no response yet)
function statusChip(e) {
    if (e.type === 'req' || e.type === 'snap' || e.type === 'evt' || e.type === 'lumi' || e.type === 'fn' || e.type === 'reverb') return null
    const s = e.status ?? 0
    if (e.type === 'err' || s >= 500) return { text: String(s || 'ERR'), cls: 'bg-red-800 text-red-200' }
    if (s >= 400) return { text: String(s), cls: 'bg-orange-800 text-orange-200' }
    if (s >= 200 && s < 300) return { text: String(s), cls: 'bg-green-800 text-green-200' }
    return { text: String(s) || '?', cls: 'bg-slate-600 text-slate-200' }
}

function toggle(id) { expand.value = expand.value === id ? null : id }
function fmt(v)  { try { return JSON.stringify(v, null, 2) } catch { return String(v) } }

function copyForClaude() {
    const parts = []
    for (const [label, snap] of [['A', store.snapA], ['B', store.snapB]]) {
        if (!snap) continue
        parts.push(`=== SNAPSHOT ${label} — ${snap.url} — ${snap.time} — ${snap.entries.length} entries ===`)
        const ordered = [...snap.entries].reverse()
        for (const e of ordered) {
            const typePad = sourceChip(e).text.padEnd(5)
            const sc = statusChip(e)
            const statusStr = sc ? ` ${sc.text}` : ''
            parts.push(`${e.time}  ${typePad}  ${e.label}${statusStr}`)
        }
        parts.push('')
    }
    if (!parts.length) return
    navigator.clipboard.writeText(parts.join('\n')).then(() => {
        copiedClaude.value = true
        setTimeout(() => { copiedClaude.value = false }, 2000)
    })
}

function copyEntry(e, onlyDetail = false) {
    const text = onlyDetail ? fmt(e.detail) : `[${e.time}] ${e.label}\n\n${fmt(e.detail)}`
    navigator.clipboard.writeText(text).then(() => {
        copied.value = e.id + (onlyDetail ? '_d' : '_f')
        setTimeout(() => { copied.value = null }, 1500)
    })
}

// ── Query table helpers ───────────────────────────────────────────────────────
function isSlow(ms) { return ms > 100 }
</script>

<template>
    <!-- Fullscreen overlay -->
    <div v-if="store.open" class="fixed inset-0 z-[99999] bg-[#0d1117] flex flex-col">

        <!-- Header -->
        <div class="flex items-center gap-2 px-4 py-2.5 border-b border-[#30363d] flex-shrink-0 flex-wrap">
            <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0"></span>
            <span class="text-[12px] font-mono font-bold text-green-400 tracking-widest">DEV CONSOLE</span>
            <div class="flex gap-1 ml-2">
                <button v-for="f in filters" :key="f"
                    @click="store.filter = f"
                    :class="['px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wide transition-colors cursor-pointer',
                        store.filter === f ? 'bg-slate-600 text-white' : 'text-slate-500 hover:text-slate-300']">{{ f }}</button>
            </div>

            <!-- Search -->
            <input v-model="search" type="text" placeholder="Search…"
                class="ml-2 px-2.5 py-1 rounded bg-[#161b22] border border-[#30363d] text-[11px] font-mono text-slate-300 placeholder-slate-600 outline-none focus:border-slate-500 w-48" />

            <div class="flex-1"/>

            <!-- Snapshot A / B slots -->
            <div class="flex items-center gap-1 mr-2">
                <button @click="store.saveSnap('A')"
                    :class="['px-2.5 py-1 rounded border text-[10px] font-mono font-bold cursor-pointer transition-colors',
                        store.snapA ? 'bg-sky-900/60 border-sky-600 text-sky-300' : 'bg-slate-800/60 border-slate-600 text-slate-500 hover:text-slate-300']">
                    {{ store.snapA ? 'A ✓' : 'Snap A' }}
                </button>
                <button @click="store.saveSnap('B')"
                    :class="['px-2.5 py-1 rounded border text-[10px] font-mono font-bold cursor-pointer transition-colors',
                        store.snapB ? 'bg-sky-900/60 border-sky-600 text-sky-300' : 'bg-slate-800/60 border-slate-600 text-slate-500 hover:text-slate-300']">
                    {{ store.snapB ? 'B ✓' : 'Snap B' }}
                </button>
                <button @click="copyForClaude" :disabled="!store.snapA && !store.snapB"
                    :class="['px-2.5 py-1 rounded border text-[10px] font-mono cursor-pointer transition-colors',
                        copiedClaude ? 'bg-green-900/60 border-green-600 text-green-300'
                        : (store.snapA || store.snapB) ? 'bg-violet-900/60 border-violet-600 text-violet-300 hover:bg-violet-800/60'
                        : 'bg-slate-800/40 border-slate-700 text-slate-600 cursor-not-allowed']">
                    {{ copiedClaude ? '✓ Copied!' : 'Copy for Claude' }}
                </button>
            </div>

            <button @click="takeSnapshot" :disabled="snapping"
                class="flex items-center gap-1.5 px-3 py-1 rounded bg-indigo-900/50 border border-indigo-700/60 text-[11px] font-mono text-indigo-300 hover:bg-indigo-800/60 hover:text-indigo-200 cursor-pointer transition-colors mr-1 disabled:opacity-50">
                {{ snapping ? '...' : '⚡ SNAP' }}
            </button>
            <span class="text-[11px] text-slate-500 font-mono mr-3">{{ filtered.length }}/{{ store.entries.length }}</span>
            <button @click="store.clear()"
                class="flex items-center gap-1.5 px-3 py-1 rounded bg-red-900/40 border border-red-800/60 text-[11px] font-mono text-red-400 hover:bg-red-800/60 cursor-pointer transition-colors mr-2">
                Clear all
            </button>
            <button @click="store.open = false"
                class="px-2.5 py-1 rounded bg-slate-700/50 border border-slate-600 text-[11px] font-mono text-slate-300 hover:bg-slate-600 cursor-pointer transition-colors">
                ✕ Close
            </button>
        </div>

        <!-- Entries -->
        <div class="flex-1 overflow-y-auto font-mono text-[11px]">
            <div v-if="filtered.length === 0" class="text-center text-slate-600 py-16 text-[13px]">
                {{ search ? 'No matches for "' + search + '"' : 'No entries yet' }}
            </div>

            <div v-for="e in filtered" :key="e.id">
                <!-- Row -->
                <div class="flex items-center border-b border-[#21262d] hover:bg-[#161b22] group">
                    <button @click="toggle(e.id)" class="flex-1 flex items-center gap-3 px-4 py-2 text-left cursor-pointer">
                        <span class="text-slate-600 flex-shrink-0 w-[60px]">{{ e.time }}</span>
                        <span class="flex items-center gap-1 flex-shrink-0">
                            <span :class="['px-1.5 py-px rounded text-[10px] font-bold', sourceChip(e).cls]">{{ sourceChip(e).text }}</span>
                            <span v-if="statusChip(e)" :class="['px-1.5 py-px rounded text-[10px] font-bold', statusChip(e).cls]">{{ statusChip(e).text }}</span>
                        </span>
                        <span class="flex-1 min-w-0 truncate">
                            <span class="text-slate-300 truncate">{{ e.label }}</span>
                        </span>
                    </button>
                    <div class="flex items-center gap-1 pr-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button @click.stop="copyEntry(e, true)"
                            :class="['px-2 py-0.5 rounded text-[10px] font-mono border cursor-pointer transition-colors',
                                copied === e.id+'_d' ? 'bg-green-900/50 border-green-700 text-green-400' : 'bg-slate-800 border-slate-600 text-slate-400 hover:text-slate-200']">
                            {{ copied === e.id+'_d' ? '✓' : 'data' }}
                        </button>
                        <button @click.stop="copyEntry(e, false)"
                            :class="['px-2 py-0.5 rounded text-[10px] font-mono border cursor-pointer transition-colors',
                                copied === e.id+'_f' ? 'bg-green-900/50 border-green-700 text-green-400' : 'bg-slate-800 border-slate-600 text-slate-400 hover:text-slate-200']">
                            {{ copied === e.id+'_f' ? '✓' : 'full' }}
                        </button>
                    </div>
                </div>

                <!-- Expanded detail -->
                <div v-if="expand === e.id" class="bg-[#0a0e13] border-b border-[#30363d]">

                    <!-- FN trace entry -->
                    <!-- Reverb/WebSocket event entry -->
                    <div v-if="e.type === 'reverb'" class="p-4 space-y-3">
                        <div class="flex gap-4 text-[11px] font-mono">
                            <span class="text-slate-500">channel</span>
                            <span class="text-cyan-300">{{ e.detail?.channel }}</span>
                        </div>
                        <div class="flex gap-4 text-[11px] font-mono">
                            <span class="text-slate-500">event&nbsp;&nbsp;</span>
                            <span class="text-cyan-300">{{ e.detail?.event }}</span>
                        </div>
                        <div v-if="e.detail?.data">
                            <div class="text-[11px] text-slate-600 uppercase tracking-widest mb-1">Payload</div>
                            <DevJsonTree :data="e.detail.data" />
                        </div>
                    </div>

                    <div v-if="e.type === 'fn'" class="p-4">
                        <div class="text-[11px] text-slate-600 uppercase tracking-widest mb-2">Arguments</div>
                        <div v-if="!e.detail?.args?.length" class="text-slate-600 text-[11px] font-mono">none</div>
                        <div v-else class="space-y-1">
                            <div v-for="(arg, i) in e.detail.args" :key="i"
                                class="flex items-baseline gap-2 font-mono text-[11px]">
                                <span class="text-slate-600 w-4 text-right flex-shrink-0">{{ i }}</span>
                                <span class="text-emerald-300 break-all">{{ arg }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- LUMI chat entry -->
                    <div v-if="e.type === 'lumi'" class="p-4 space-y-2">
                        <div v-if="e.detail?.message" class="bg-[#1a1f2e] rounded p-3">
                            <div class="text-[10px] text-violet-400 font-bold mb-1">OUTGOING MESSAGE</div>
                            <pre class="text-[12px] whitespace-pre-wrap font-mono text-blue-300">{{ e.detail.message }}</pre>
                        </div>
                        <div v-if="e.detail?.reply" class="bg-[#142414] rounded p-3">
                            <div class="text-[10px] text-green-400 font-bold mb-1">LUMI REPLY<span v-if="e.detail.model" class="ml-2 text-slate-500 font-normal">{{ e.detail.provider ? e.detail.provider + ':' : '' }}{{ e.detail.model }}</span></div>
                            <pre class="text-[12px] whitespace-pre-wrap font-mono text-green-300">{{ e.detail.reply }}</pre>
                        </div>
                        <div v-if="e.detail?.endpoint" class="text-[10px] text-slate-600">endpoint: {{ e.detail.endpoint }}</div>
                        <div v-if="e.detail?.conversation_id" class="text-[10px] text-slate-600">conversation: {{ e.detail.conversation_id }}</div>
                    </div>

                    <!-- SNAP structured view -->
                    <div v-if="e.type === 'snap'" class="p-4 space-y-4">

                        <div class="flex justify-end">
                            <button @click="copyEntry(e, true)"
                                :class="['px-3 py-1 rounded text-[10px] font-mono border cursor-pointer transition-colors',
                                    copied === e.id+'_d' ? 'bg-green-900/50 border-green-700 text-green-400' : 'bg-slate-800 border-slate-600 text-slate-400 hover:text-slate-200']">
                                {{ copied === e.id+'_d' ? '✓ Copied!' : 'Copy all JSON' }}
                            </button>
                        </div>

                        <!-- Memory -->
                        <div>
                            <div class="text-amber-400 font-bold mb-1">PHP Memory</div>
                            <div class="flex gap-6 text-slate-300">
                                <span>Current: <span class="text-green-300">{{ e.detail?.memory?.current_human }}</span></span>
                                <span>Peak: <span class="text-yellow-300">{{ e.detail?.memory?.peak_human }}</span></span>
                            </div>
                        </div>

                        <!-- Auth -->
                        <div>
                            <div class="text-amber-400 font-bold mb-1">Auth</div>
                            <div class="pl-2 text-[12px] leading-relaxed">
                                <DevJsonTree :value="e.detail?.authState" />
                            </div>
                        </div>

                        <!-- Session -->
                        <div>
                            <div class="text-amber-400 font-bold mb-1">Session</div>
                            <div class="pl-2 text-[12px] leading-relaxed max-h-48 overflow-y-auto">
                                <DevJsonTree :value="e.detail?.session" />
                            </div>
                        </div>

                        <!-- Queries -->
                        <div>
                            <div class="text-amber-400 font-bold mb-2">
                                Queries ({{ e.detail?.queries?.length ?? 0 }})
                            </div>
                            <div v-if="!e.detail?.queries?.length" class="text-slate-600">No queries captured</div>
                            <div v-else class="space-y-1.5 max-h-[50vh] overflow-y-auto">
                                <div v-for="(q, i) in e.detail.queries" :key="i"
                                    :class="['rounded px-3 py-2 border', isSlow(q.time_ms)
                                        ? 'border-red-800/60 bg-red-950/30'
                                        : 'border-[#21262d] bg-[#161b22]']">
                                    <div class="flex items-center gap-2 mb-1">
                                        <span class="text-slate-600 text-[10px]">#{{ i + 1 }}</span>
                                        <span :class="['text-[10px] font-bold', isSlow(q.time_ms) ? 'text-red-400' : 'text-green-500']">
                                            {{ q.time_ms?.toFixed(2) }}ms
                                        </span>
                                    </div>
                                    <div class="text-blue-300 break-all">{{ q.sql }}</div>
                                    <div v-if="q.bindings?.length" class="text-slate-500 mt-1">
                                        bindings: {{ JSON.stringify(q.bindings) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Default: collapsible JSON tree view -->
                    <div v-else class="px-6 py-4 space-y-4">
                        <div class="flex justify-end">
                            <button @click="copyEntry(e, true)"
                                :class="['px-3 py-1 rounded text-[10px] font-mono border cursor-pointer transition-colors',
                                    copied === e.id+'_d' ? 'bg-green-900/50 border-green-700 text-green-400' : 'bg-slate-800 border-slate-600 text-slate-400 hover:text-slate-200']">
                                {{ copied === e.id+'_d' ? '✓ Copied!' : 'Copy JSON' }}
                            </button>
                        </div>

                        <!-- Caller stack — resolved async via source maps -->
                        <div v-if="e.type === 'php' && e.caller?.length" class="mb-3">
                            <div class="text-[11px] text-slate-600 uppercase tracking-widest mb-1.5">Called from</div>
                            <div class="space-y-1">
                                <div v-for="frame in e.caller" :key="frame.file + frame.line"
                                    class="flex items-baseline gap-2 font-mono">
                                    <span v-if="frame.name" class="text-amber-300 text-[10px] flex-shrink-0">{{ frame.name }}</span>
                                    <span class="text-slate-400 text-[10px] truncate">
                                        {{ frame.file }}<span class="text-slate-600">:{{ frame.line }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Queries table — shown whenever the payload has a queries array -->
                        <div v-if="e.detail?.queries?.length">
                            <div class="text-amber-400 font-bold font-mono mb-2">
                                Queries ({{ e.detail.queries.length }})
                            </div>
                            <div class="space-y-1.5 max-h-64 overflow-y-auto">
                                <div v-for="(q, i) in e.detail.queries" :key="i"
                                    :class="['rounded px-3 py-2 border', isSlow(q.time_ms)
                                        ? 'border-red-800/60 bg-red-950/30'
                                        : 'border-[#21262d] bg-[#161b22]']">
                                    <div class="flex items-center gap-2 mb-1">
                                        <span class="text-slate-600 text-[10px] font-mono">#{{ i + 1 }}</span>
                                        <span :class="['text-[10px] font-bold font-mono', isSlow(q.time_ms) ? 'text-red-400' : 'text-green-500']">
                                            {{ q.time_ms?.toFixed(2) }}ms
                                        </span>
                                    </div>
                                    <div class="text-blue-300 break-all font-mono text-[11px]">{{ q.sql }}</div>
                                    <div v-if="q.bindings?.length" class="text-slate-500 font-mono text-[10px] mt-1">
                                        bindings: {{ JSON.stringify(q.bindings) }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Outgoing HTTP calls -->
                        <div v-if="e.detail?.http_calls?.length">
                            <div class="text-amber-400 font-bold font-mono mb-2">
                                Outgoing HTTP ({{ e.detail.http_calls.length }})
                            </div>
                            <div class="space-y-2">
                                <div v-for="(h, i) in e.detail.http_calls" :key="i"
                                    :class="['rounded border',
                                        h.status >= 500 ? 'border-red-800/60 bg-red-950/30'
                                        : h.status >= 400 ? 'border-orange-800/60 bg-orange-950/20'
                                        : 'border-[#21262d] bg-[#161b22]']">
                                    <!-- header row -->
                                    <div class="flex items-center gap-2 px-3 py-2">
                                        <span :class="['text-[10px] font-bold font-mono w-8 flex-shrink-0',
                                            h.method === 'POST' ? 'text-green-400'
                                            : h.method === 'GET' ? 'text-blue-400'
                                            : 'text-slate-400']">{{ h.method }}</span>
                                        <span :class="['text-[10px] font-bold px-1.5 py-px rounded flex-shrink-0',
                                            h.status >= 500 ? 'bg-red-800 text-red-200'
                                            : h.status >= 400 ? 'bg-orange-800 text-orange-200'
                                            : 'bg-green-800 text-green-200']">{{ h.status }}</span>
                                        <span class="text-slate-300 font-mono text-[11px] flex-1 truncate">{{ h.url }}</span>
                                        <span :class="['text-[10px] font-bold flex-shrink-0',
                                            h.ms > 1000 ? 'text-red-400' : h.ms > 300 ? 'text-amber-400' : 'text-green-500']">{{ h.ms }}ms</span>
                                    </div>
                                    <!-- request body -->
                                    <div v-if="h.body" class="border-t border-[#21262d] px-3 py-2">
                                        <div class="text-[11px] text-slate-600 uppercase tracking-widest mb-1">Request body</div>
                                        <DevJsonTree :value="h.body" :depth="0" />
                                    </div>
                                    <!-- response body -->
                                    <div v-if="h.response" class="border-t border-[#21262d] px-3 py-2">
                                        <div class="text-[11px] text-slate-600 uppercase tracking-widest mb-1">Response body</div>
                                        <DevJsonTree :value="h.response" :depth="0" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="text-[12px] leading-relaxed overflow-y-auto" style="max-height:55vh">
                            <DevJsonTree :value="e.detail" :depth="0" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Draggable toggle button -->
    <div class="fixed z-[99998]" :style="{ left: pos.x + 'px', top: pos.y + 'px' }">
        <div class="flex items-center rounded-lg overflow-hidden shadow-lg border border-[#30363d] bg-[#0d1117]">
            <div @mousedown.prevent="onDragStart" @touchstart.prevent="onDragStart"
                class="flex items-center justify-center w-5 h-full px-1 cursor-grab active:cursor-grabbing border-r border-[#30363d] text-slate-600 hover:text-slate-400 select-none">
                <svg width="6" height="14" viewBox="0 0 6 14" fill="currentColor">
                    <circle cx="1.5" cy="2"  r="1.5"/>
                    <circle cx="4.5" cy="2"  r="1.5"/>
                    <circle cx="1.5" cy="7"  r="1.5"/>
                    <circle cx="4.5" cy="7"  r="1.5"/>
                    <circle cx="1.5" cy="12" r="1.5"/>
                    <circle cx="4.5" cy="12" r="1.5"/>
                </svg>
            </div>
            <button @click="store.open = !store.open"
                class="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono text-green-400 hover:text-green-300 cursor-pointer transition-colors">
                <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0"></span>
                DEV
                <span v-if="store.entries.length" class="bg-slate-700 text-slate-300 rounded px-1 text-[10px]">{{ store.entries.length }}</span>
            </button>
            <button @click="takeSnapshot" :disabled="snapping"
                class="px-2 py-1.5 border-l border-[#30363d] text-[11px] font-mono text-indigo-400 hover:text-indigo-300 cursor-pointer transition-colors disabled:opacity-40"
                title="Snapshot session + queries">
                {{ snapping ? '…' : '⚡' }}
            </button>
        </div>
    </div>
</template>
